import {BOOKING_CONSTRAINTS} from '@/constants/booking'

/**
 * Application timezone for the whole booking flow. Sourced from the .env
 * VITE_APP_TIMEZONE value; falls back to America/Toronto. Mirrors the Laravel
 * backend's APP_TIMEZONE so the naive `Y-m-d H:i:s` scheduled_at we send is
 * interpreted in the same zone.
 *
 * The env var was bare `APP_TIMEZONE` until 2026-08-26, which needed a custom
 * `envPrefix` in vite.config.js and did not match the driver / phone portals.
 * All four now use VITE_APP_TIMEZONE. (The exported constant keeps its name —
 * it is a JS identifier, read in a lot of places, and unrelated to the env key.)
 *
 * NO default. `.env` is the single source — it is not this file's job to invent a zone
 * the environment failed to supply. `|| ''` is a normaliser, not a fallback: it turns an
 * absent var into a value Intl REJECTS. That is deliberate. `timeZone: undefined` makes
 * Intl silently format in the VIEWER's zone, which is the worst outcome here — in Toronto
 * it looks perfectly correct and the bug only appears for users elsewhere. `timeZone: ''`
 * throws `RangeError: Invalid time zone specified:` on the first format instead, so a
 * missing var is impossible to miss.
 */
export const APP_TIMEZONE = import.meta.env.VITE_APP_TIMEZONE || ''

/**
 * Build a Date whose LOCAL field getters (getFullYear/getMonth/getDate/
 * getHours/getMinutes/...) return the current wall-clock time in `timeZone`.
 *
 * The returned Date's epoch (getTime) is intentionally shifted — it is NOT the
 * real instant — but every booking-flow consumer reads wall-clock getters and
 * serializes them as a naive `Y-m-d H:i:s` string, so the shift is the whole
 * point: the picker and lead-time checks operate in APP_TIMEZONE regardless of
 * the visitor's browser timezone.
 * @param {string} timeZone IANA timezone, e.g. "America/Toronto"
 * @returns {Date}
 */
function nowInTimeZone(timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
    }).formatToParts(new Date())

    const map = {}
    for (const p of parts) map[p.type] = p.value

    let hour = parseInt(map.hour, 10)
    if (hour === 24) hour = 0 // some engines emit "24" for midnight

    return new Date(
        parseInt(map.year, 10),
        parseInt(map.month, 10) - 1,
        parseInt(map.day, 10),
        hour,
        parseInt(map.minute, 10),
        parseInt(map.second, 10),
        0
    )
}

/**
 * Current time in the application timezone (APP_TIMEZONE). Its wall-clock
 * getters reflect that zone, so the booking time picker and lead-time checks
 * operate in APP_TIMEZONE no matter where the visitor is.
 */
export function tripNow() {
    return nowInTimeZone(APP_TIMEZONE)
}

/**
 * How far `timeZone`'s wall clock is ahead of UTC at the instant `utcMs`, in ms.
 * (EDT → -4h, EST → -5h.) Derived by reading the instant back through Intl.
 */
function zoneOffsetMs(utcMs, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
    }).formatToParts(new Date(utcMs))

    const map = {}
    for (const p of parts) map[p.type] = p.value
    let hour = parseInt(map.hour, 10)
    if (hour === 24) hour = 0

    const asIfUtc = Date.UTC(
        parseInt(map.year, 10), parseInt(map.month, 10) - 1, parseInt(map.day, 10),
        hour, parseInt(map.minute, 10), parseInt(map.second, 10)
    )
    return asIfUtc - utcMs
}

/**
 * Parse a server timestamp into a REAL instant.
 *
 * The API sends most timestamps naive — "2026-07-20 06:15:00", no zone — and those
 * are APP_TIMEZONE wall-clock, NOT UTC (the backend runs on the same APP_TIMEZONE).
 * `new Date(str)` would read them as the visitor's local time and `str + 'Z'` as UTC;
 * both are wrong by the zone offset, which is what made "24 min ago" render as
 * "4 hr ago" for a Toronto-stamped notification.
 *
 * Strings that DO carry a zone (…Z or ±HH:MM) are already unambiguous and pass through.
 *
 * Unlike tripNow(), the returned Date is a true instant — safe for elapsed-time maths.
 * @returns {Date|null}
 */
export function parseAppZoneDate(value) {
    if (!value) return null
    if (value instanceof Date) return value

    const s = String(value).trim()
    if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s.replace(' ', 'T'))

    const m = s.replace('T', ' ').match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})(?::(\d{2}))?/)
    if (!m) return new Date(s)  // unknown shape — let the engine try

    const guessUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0))
    // Two passes: the offset at the guessed instant can differ from the offset at the
    // real one within a DST switch, so re-read it once the guess has been corrected.
    const firstPass = zoneOffsetMs(guessUtc, APP_TIMEZONE)
    const offset = zoneOffsetMs(guessUtc - firstPass, APP_TIMEZONE)
    return new Date(guessUtc - offset)
}

/**
 * A server timestamp shifted by `minutes`, as a REAL instant.
 *
 * Replaces the `new Date(new Date(trip.scheduled_at).getTime() + m * 60000)` idiom that
 * was repeated at every estimated-arrival / leg-end site. That idiom's inner `new Date()`
 * reads a naive server string as the VISITOR's local time, producing a Date whose epoch is
 * wrong by the zone offset — harmless while the result was only ever read back through
 * local getters, but silently wrong now that these values are rendered in the viewer's
 * zone. Going through parseAppZoneDate makes the base a true instant, so the derived one
 * is too.
 * @returns {Date|null}
 */
export function addMinutesToAppZone(value, minutes) {
    const base = parseAppZoneDate(value)
    if (!base || isNaN(base.getTime())) return null
    return new Date(base.getTime() + (Number(minutes) || 0) * 60000)
}

/**
 * Midnight of the APP_TIMEZONE calendar day containing `date`, as a comparable
 * UTC-based key. Use for "is this today?" — the visitor's own midnight is irrelevant.
 */
export function appZoneStartOfDay(date) {
    if (!date) return NaN
    const [y, m, d] = new Intl.DateTimeFormat('en-CA', {
        timeZone: APP_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date).split('-')
    return Date.UTC(+y, +m - 1, +d)
}

export function formatTripLocalTime(date) {
    if (!date) return '--'
    const h = date.getHours()
    const m = date.getMinutes()
    const ampm = h >= 12 ? 'PM' : 'AM'
    const display = h % 12 || 12
    return `${display}:${String(m).padStart(2, '0')} ${ampm}`
}

export function to24h(hour, period) {
    const h = parseInt(hour, 10)
    if (period === 'AM') return h === 12 ? 0 : h
    return h === 12 ? 12 : h + 12
}

export function buildDateOptionsFromRange(min, max) {
    const dates = []
    const current = new Date(min)
    current.setHours(0, 0, 0, 0)
    const end = new Date(max)
    end.setHours(0, 0, 0, 0)
    while (current <= end) {
        dates.push({
            iso: current.toISOString(),
            value: current.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'}),
            label: current.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'}),
        })
        current.setDate(current.getDate() + 1)
    }
    return dates
}

export function roundToNext5Min(date) {
    const d = new Date(date)
    let m = d.getMinutes()
    const rem = m % 5
    if (rem !== 0) m = m + (5 - rem)
    if (m >= 60) { m = 0; d.setHours(d.getHours() + 1) }
    d.setMinutes(m, 0, 0)
    return d
}

export function getNowRef() {
    const now = roundToNext5Min(tripNow())
    return {hour24: now.getHours(), minute: now.getMinutes()}
}

export function getMinLeadTimeStr() {
    return formatDHM(BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES)
}

/**
 * THE duration formatter for the portal — days / hours / minutes, largest unit first.
 *
 *   < 60m          →  "45m"
 *   < 24h          →  "5h"        /  "5h 30m"
 *   >= 24h         →  "2d"        /  "2d 14h"  /  "2d 14h 16m"
 *
 * Zero parts are dropped, so it never reads "2d 0h 16m". Rolling into DAYS is the
 * whole point: an hours-only formatter rendered a 2½-day countdown as "62h 16m".
 *
 * `unit` says what `value` is in — 'minutes' (default), 'hours' or 'seconds'. The API
 * mixes them (hours_until_pickup is decimal hours, route legs are minutes), so the
 * conversion lives here rather than at each call site.
 *
 * Returns '' for null/undefined/NaN so callers can render it straight into JSX;
 * an explicit 0 gives "0m".
 */
export function formatDHM(value, unit = 'minutes') {
    if (value == null || isNaN(Number(value))) return ''
    const perUnit = unit === 'hours' ? 60 : unit === 'seconds' ? 1 / 60 : 1
    // Round to whole minutes FIRST: rounding each part separately lets 1.99h show as
    // "1h 59m" in one place and "2h" in another.
    const total = Math.max(0, Math.round(Number(value) * perUnit))

    const d = Math.floor(total / 1440)
    const h = Math.floor((total % 1440) / 60)
    const m = total % 60

    const parts = []
    if (d) parts.push(`${d}d`)
    if (h) parts.push(`${h}h`)
    // Minutes shown unless a bigger unit already carries the value (so "2d" not "2d 0m"),
    // and always for a sub-minute/zero duration so something renders.
    if (m || !parts.length) parts.push(`${m}m`)
    return parts.join(' ')
}

// Kept as the name three smart-time call sites already import. Same output as
// formatDHM now — the old "5hr 30min" spelling is gone in favour of one format.
export function formatDurationHM(minutes) {
    return formatDHM(minutes)
}

/**
 * Parse a stored RIDE_DATE into a LOCAL calendar date at midnight.
 *
 * RIDE_DATE is written as a bare "YYYY-MM-DD" (travelingDateTime, smartPickupResult).
 * `new Date("2026-09-01")` is the ISO DATE-ONLY form, which the spec says to parse as
 * UTC midnight — but every consumer reads it back with LOCAL getters
 * (getFullYear/getMonth/getDate). West of UTC those getters return the PREVIOUS day, so
 * a pickup picked for Sep 1 was stored fine and read back as Aug 31: the wheel could not
 * match the stored day against its own options and fell back to its default, and
 * scheduled_at went to the API a day early.
 *
 * Splitting the string and using the local Date(y, m, d) constructor keeps the calendar
 * day the user picked, in every timezone. Other shapes — a full ISO instant, a
 * Date.prototype.toString() — carry their own zone and pass through unchanged.
 * @returns {Date|null}
 */
export function parseRideDate(value) {
    if (!value) return null
    // Always a fresh Date — callers routinely do `d.setHours(0,0,0,0)` on the result,
    // and handing back the caller's own object would mutate it under them.
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value.getTime())

    const s = String(value).trim()
    const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3])

    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
}

/** "YYYY-MM-DD" for a stored RIDE_DATE, in the calendar day the user actually picked. */
export function rideDateYMD(value) {
    const d = parseRideDate(value)
    if (!d) return ''
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
